import { cn } from '@/shared/lib';
import { formatCount, formatRelativeTime } from '@/shared/format';
import {
  assistantHistoryClasses,
  assistantLogEmptyClasses,
  assistantThreadClasses,
  assistantThreadMetaClasses,
  assistantThreadSelectedClasses,
  assistantThreadTitleClasses,
} from '@/modules/assistant/parts/styles';
import type { AssistantThread } from '@/api/schemas/assistant';

export type ThreadListProps = {
  /** The caller's conversations, newest first. */
  threads: AssistantThread[];
  /** The open conversation, or null when there is none. */
  selectedThreadId: string | null;
  /** Opens a conversation. */
  onSelect: (threadId: string) => void;
};

/**
 * The caller's conversations.
 *
 * The list is the SERVER's — the panel reads it from the API on mount, which is
 * what makes the history survive a reload and survive a different browser. The
 * console keeps no copy of it.
 *
 * The count shown is the API's `messageCount`, labelled as MESSAGES, because
 * that is what it counts. Calling it a number of turns would name a number the
 * server never sent.
 */
export function ThreadList({ threads, selectedThreadId, onSelect }: ThreadListProps) {
  return (
    <div className={assistantHistoryClasses} aria-label="Conversations">
      {threads.length === 0 ? (
        <p className={assistantLogEmptyClasses}>No conversations yet.</p>
      ) : null}

      {threads.map((thread) => {
        const isSelected = thread.id === selectedThreadId;
        return (
          <button
            key={thread.id}
            type="button"
            aria-current={isSelected ? 'true' : undefined}
            onClick={() => onSelect(thread.id)}
            className={cn(assistantThreadClasses, isSelected && assistantThreadSelectedClasses)}
          >
            <span className={assistantThreadTitleClasses}>{thread.title}</span>
            <span className={assistantThreadMetaClasses}>
              {formatCount(thread.messageCount)} messages - {formatRelativeTime(thread.updatedAt)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
